import { Injectable } from '@angular/core';
import { AlertService } from '../../../core/services/alert.service';
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, map, Observable, switchMap } from 'rxjs';
import { CustomerServiceInsightsRequestsService } from 'src/app/core/services/customer-service-insights-requests.service';
import { Guid } from 'guid-typescript';
import { AxiosResponse } from 'axios';
import { MonthlyPerformanceData, Period, LocationInsightsAdvancedFilters} from '../types/customer-types';
import { AuthenticationEvent, AuthenticationService } from 'src/app/core/services/authentication.service';

export type LocationPerformanceData = any;
export type IssuesCountData = any;
export type LocationInsightsData = any;
export type LocationDetailsData = any;
export type TotalCallsByTypeData = any;

@Injectable({
  providedIn: 'root'
})
export class CustomerServiceInsightsService {

  private readonly REGION = 'NA';

  //both period and accountNumbers are combined in one observable, since changes to either should trigger the endpoint requests
  //additionally since advancedFilters do not persist between period changes, both need to be updated at the same time
  private periodAndAccountNumbersSubject = new BehaviorSubject<{period: Period, accountNumbers: string[]}>({period: Period['12 Months'], accountNumbers: []});
  periodSelectedAndAccountNumbers$: Observable<{period:Period, accountNumbers: string[]}> = this.periodAndAccountNumbersSubject.asObservable().pipe(
    debounceTime(500),
    distinctUntilChanged(),
  );

  //for the detail page
  private accountNumberSubject = new BehaviorSubject<string | null>(null);
  accountNumberSelected$: Observable<string | null> = this.accountNumberSubject.asObservable().pipe(
    distinctUntilChanged()
  );

  private graphDetailFilterSubject = new BehaviorSubject<string>("");
  graphDetailFilterSelected$: Observable<string | undefined> = this.graphDetailFilterSubject.asObservable().pipe(
    distinctUntilChanged()
  );

  private pageNumberSubject = new BehaviorSubject<number>(1);
  pageNumberSelected$: Observable<number> = this.pageNumberSubject.asObservable().pipe(
    distinctUntilChanged()
  );

  private pageSizeSubject = new BehaviorSubject<number>(100);
  pageSizeSelected$: Observable<number> = this.pageSizeSubject.asObservable().pipe(
    distinctUntilChanged()
  );

  //maintains selections as user navigates between details page and landing page
  private advancedFiltersSubject = new BehaviorSubject<LocationInsightsAdvancedFilters>({
    "accountName": [],
    "city": [],
    "state": [],
    "performanceRating": [],
  });
  selectedAdvancedFilters$: Observable<LocationInsightsAdvancedFilters> = this.advancedFiltersSubject.asObservable().pipe(
    distinctUntilChanged()
  );

  constructor(
    private _customerSIService: CustomerServiceInsightsRequestsService,
    private _alertService : AlertService,
    private _authenticationService: AuthenticationService
  ) {
    // TODO: Consider using subject in the service
    _authenticationService.events.subscribe(
      AuthenticationEvent.ImpersonationStarted,
      () => {
        this.resetData();
      });
    _authenticationService.events.subscribe(
      AuthenticationEvent.ImpersonationStopped,
      () => {
        this.resetData()
      }
    );
  }


  //for the detail page
  setAccountNumber(accountNumber: string | null) : void{
    this.accountNumberSubject.next(accountNumber);
  }

  setGraphDetailFilter(obsSelected: string): void {
    this.graphDetailFilterSubject.next(obsSelected);
  }

  getCurrentGraphDetailFilter(): string | undefined {
    return this.graphDetailFilterSubject.getValue();
  }

  setPageNumber(pageNumber: number): void {
    this.pageNumberSubject.next(pageNumber);
  }

  setPageSize(pageSize: number): void {
    this.pageSizeSubject.next(pageSize);
  }

  setSelectedAdvancedFilters(selectedAdvancedFilters: LocationInsightsAdvancedFilters): void {
    this.advancedFiltersSubject.next(selectedAdvancedFilters);
  }

  setPeriodAndAccountNumbers(period: Period, accountNumbers: string[]): void {
    this.resetData();
    this.periodAndAccountNumbersSubject.next({period, accountNumbers});
  }
  setPeriod(period: Period): void {
    this.setPeriodAndAccountNumbers(period, this.periodAndAccountNumbersSubject.getValue().accountNumbers);
  }
  setAccountNumbers(accountNumbers: string[]): void {
    this.setPeriodAndAccountNumbers(this.periodAndAccountNumbersSubject.getValue().period, accountNumbers);
  }

  resetData() {
    this._allLocationsPerformanceData = null;
    this._singleLocationPerformanceData = null;
    this._allLocationsIssuesCountData = null;
    this._singleLocationIssuesCountData = null;
    this._locationInsightsData = null;
    this._locationDetailsData = null;
    this._totalCallsByTypeData = null;
  }

  // Get Customer Locations Performance Data
  private _allLocationsPerformanceData: Observable<LocationPerformanceData> | null = null;
  public get allLocationsPerformanceData$(): Observable<LocationPerformanceData> {
    if (this._allLocationsPerformanceData == null) {
      this._allLocationsPerformanceData = this.periodSelectedAndAccountNumbers$.pipe(
        switchMap(combinedObject =>
          this._customerSIService.getLocationsPerformanceData(this.REGION, combinedObject.period, null, combinedObject.accountNumbers)
        ),
        map((response: AxiosResponse) => {
          const data = response.data;
          return {
            gaugeNeedleValue: data.overallPerformanceRating,
            lineChartData: {
              xAxis: data.monthlyData.map((item: any) => item.month),
              yAxis: data.monthlyData.map((data: MonthlyPerformanceData) => {
                if (data.noCallData) {
                  return { y: data.performanceRating, noCallData: true };
                }
                return data.performanceRating;
              })
            },
            serviceMetricsChartData: [data.recurringIssuesCount, data.issuesCount, data.oopCount],
            totalIssuesData: data.issuesCount === 0 ? [0, 0] : [
              Math.round(data.recurringIssuesCount / data.issuesCount * 100),
              Math.round((data.newIssuesCount) / data.issuesCount * 100)
            ],
            totalIssuesMiddleText: data.issuesCount,
            hasZeroServiceData: data.recurringIssuesCount === 0 && data.issuesCount === 0 && data.oopCount === 0
          };
        }),
        catchError(error => {
          this.handleError('Failed to fetch locations performance data', error);
          return [];
        })
      )
    }
    return this._allLocationsPerformanceData;
  }

  // Get Account Specific Location Performance Data
  private _singleLocationPerformanceData: Observable<LocationPerformanceData> | null = null;
  public get singleLocationPerformanceData$(): Observable<LocationPerformanceData> {
    if (this._singleLocationPerformanceData == null) {
      this._singleLocationPerformanceData = this.accountNumberSelected$.pipe(
        switchMap(accountNumber =>
          this._customerSIService.getLocationsPerformanceData(this.REGION, this.periodAndAccountNumbersSubject.getValue().period, accountNumber, null)
        ),
        map((response: AxiosResponse) => {
          const data = response.data;
          return {
            gaugeNeedleValue: data.overallPerformanceRating,
            lineChartData: {
              xAxis: data.monthlyData.map((item: any) => item.month),
              yAxis: data.monthlyData.map((data: MonthlyPerformanceData) => {
                if (data.noCallData) {
                  return { y: data.performanceRating, noCallData: true };
                }
                return data.performanceRating;
              })
            }
          };
        }),
        catchError(error => {
          this.handleError('Failed to fetch locations performance data', error);
          return [];
        })
      )
    }
    return this._singleLocationPerformanceData;
  }

  // Get All Locations Observations/Issues Count Data
  private _allLocationsIssuesCountData: Observable<IssuesCountData> | null = null;
  public get allLocationsIssuesCountData$(): Observable<IssuesCountData> {
    if (this._allLocationsIssuesCountData == null) {
      this._allLocationsIssuesCountData = this.periodSelectedAndAccountNumbers$.pipe(
        switchMap(combinedObject => this._customerSIService.getIssuesCountData(this.REGION, combinedObject.period, null, combinedObject.accountNumbers)
        ),
        map((response: AxiosResponse) => {
          const data = response.data;
          return {
            comparisonChartData: data.map((item: any) => ({ calls: item.callCount, locations: item.locations })),
            comparisonChartLabels: data.map((item: any) => item.issueName)
          };
        }),
        catchError(error => {
          this.handleError('Failed to fetch issues count data', error);
          return [];
        })
      );
    }
    return this._allLocationsIssuesCountData;
  }

  // Get Account Specific Observations/Issues Count Data
  private _singleLocationIssuesCountData: Observable<IssuesCountData> | null = null;
  public get singleLocationIssuesCountData$(): Observable<IssuesCountData> {
    if (this._singleLocationIssuesCountData == null) {
      this._singleLocationIssuesCountData = this.accountNumberSelected$.pipe(
        switchMap(accountNumber => this._customerSIService.getIssuesCountData(this.REGION, this.periodAndAccountNumbersSubject.getValue().period, accountNumber, null)
        ),
        map((response: AxiosResponse) => {
          return response.data;
        }),
        catchError(error => {
          this.handleError('Failed to fetch issues count data', error);
          return [];
        }),
      );
    }
    return this._singleLocationIssuesCountData;
  }

  // Get Total Calls By Type Data
  private _totalCallsByTypeData: Observable<TotalCallsByTypeData> | null = null;
  public get totalCallsByTypeData$(): Observable<TotalCallsByTypeData> {
    if (this._totalCallsByTypeData == null) {
      this._totalCallsByTypeData = this.periodSelectedAndAccountNumbers$.pipe(
        switchMap(combinedObject => this._customerSIService.getTotalCallsByTypeData(this.REGION, combinedObject.period, combinedObject.accountNumbers)
        ),
        map((response: AxiosResponse) => {
          const data = response.data;
          const orderedCallTypes = ['Extra Service', 'Routine Service', 'Other Visits'];

          // Create a map for quick lookup
          const dataMap = new Map(data.data.map((item: any) => [item.callReason, item]));

          // Create ordered data with placeholders for missing types
          const orderedData = orderedCallTypes.map(callType =>
            dataMap.get(callType) || { callReason: callType, total: 0, totalLocations: 0 }
          );

          return {
            totalCallsChartData: orderedData.map((item: any) => ({
              calls: item.total,
              locations: item.totalLocations
            })),
            totalCallsChartLabels: orderedData.map((item: any) => item.callReason)
          };
        }),
        catchError(error => {
          this.handleError('Failed to fetch total calls by type data', error);
          return [];
        }),
      );
    }
    return this._totalCallsByTypeData;
  }

  // Get Location Insights (Top-Bottom) Data.
  private _locationInsightsData: Observable<LocationInsightsData> | null = null;
  public get locationInsightsData$(): Observable<LocationInsightsData> {
    if (this._locationInsightsData == null) {
      this._locationInsightsData = this.periodSelectedAndAccountNumbers$.pipe(
        switchMap(combinedObject =>
          this._customerSIService.getLocationInsightsData(this.REGION, combinedObject.period, combinedObject.accountNumbers)
        ),
        map((response: AxiosResponse) => {
          const data = response.data;
          if (data.statusCode === 'OK') {
            const dropdownOptions: {option: string, value: {optionName: string, value: number}}[] = [
              {option:'5', value:{optionName: '5', value: 5}},
              {option:'10', value:{optionName: '10', value: 10}},
              {option:'5%', value:{optionName: '5%', value: Math.ceil(response.data.data.length * 0.05)}},
              {option:'10%', value:{optionName: '10%', value: Math.ceil(response.data.data.length * 0.10)}}
            ];
            return {
              data: data.data,
              dropdownOptions: dropdownOptions,
              statusCode: data.statusCode
            };
          }
          else{
            return {
              data: [],
              dropdownOptions: [],
              stausCode: data.statusCode
            };
          }
        }),
        catchError(error => {
          this.handleError('Failed to fetch location insights data', error);
          return [];
        })
      );
    }
    return this._locationInsightsData;
  }

   // Get Account Specific Location Details Data
   private _locationDetailsData: Observable<IssuesCountData> | null = null;
   public get locationDetailsData$(): Observable<IssuesCountData> {
    if (this._locationDetailsData == null) {
      this._locationDetailsData = combineLatest([
        this.accountNumberSelected$,
        this.pageNumberSelected$,
        this.pageSizeSelected$,
        this.graphDetailFilterSelected$
      ]).pipe(
        switchMap(([accountNumber, pageNumber, pageSize, graphDetailFilter]) =>
          this._customerSIService.getLocationDetailsData(this.REGION,this.periodAndAccountNumbersSubject.getValue().period, accountNumber, pageNumber, pageSize, graphDetailFilter)
        ),
        map((response: AxiosResponse) => {
          const data = response.data;
          return {
            locationData: data.data.records,
            totalRows: data.data.totalCount
          };
        }),
        catchError(error => {
          this.handleError('Failed to fetch location details data', error);
          return [];
        })
      );
    }
    return this._locationDetailsData;
  }

  // Helper Methods
  private handleError(errorMessage: string, error: any) {
    console.log(error);
    this._alertService.addAlert({
      message: `${errorMessage} If problem persists, please report a bug.`,
      id: Guid.create()
    });
  }

}
